'use client';

/**
 * myfitvent sign in and sign up: one screen, two modes.
 *
 * Athlete auth is our own email (or SMS) code flow, NOT Clerk, by decision.
 * Every request goes through useParticipantAuth, unchanged:
 *
 *   POST /api/participant/auth/check-email   quiet lookup as they type
 *   POST /api/participant/auth/send-otp      sends the code (email, or SMS
 *                                            when the account has a phone)
 *   POST /api/participant/auth/verify-otp    checks it, creates the account
 *                                            if there is none, sets the
 *                                            session cookie
 *   GET  /api/participant/auth/me            the signed-in profile
 *   PUT  /api/participant/hub/profile        the optional phone, new athletes
 *
 * Sign in and sign up hit the same endpoints (verify-otp always creates a
 * missing account), so the mode changes the copy and the picture panel only.
 * The header link switches mode and keeps the email address.
 *
 * Steps: email, code, phone (new athletes only, skippable), done. A
 * returning athlete lands on `?redirect=` (same-site only) or the hub home;
 * a new one goes to profile setup, as before.
 *
 * No password, passkey, Google or Apple: participant auth supports none of
 * them, and the handoff's athlete copy promises "no password needed".
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Check, Mail, MessageSquareText, Smartphone } from 'lucide-react';
import type { Country } from 'react-phone-number-input';
import { useParticipantAuth } from '@/lib/participant-auth/useParticipantAuth';
import { useBrandPreview } from '@/lib/brand-preview/client';
import type { BrandPreviewId } from '@/lib/brand-preview/shared';
import { detectCountryFromTimezone } from '@/lib/utils/detect-country';
import { useIsMobile } from '@/hooks/use-mobile';
import { PhoneInputField } from '@/components/common/PhoneInput';
import {
  codeStepCopy,
  emailError,
  emailHint,
  emailStepCopy,
  isCompleteCode,
  parseAuthMode,
  phoneEnding,
  plainAuthError,
  resentNote,
  safeRedirect,
  sanitiseCode,
  type MyFitventAuthMode,
} from '@/lib/myfitvent-auth/form';
import {
  AthleteArtPanel,
  AuthFooter,
  AuthHeader,
  AuthIconTile,
  AuthIntro,
  AuthTitle,
  FieldMessage,
  OrganiserNote,
  PrimaryButton,
  TextAction,
  fieldClass,
} from './parts';

const NEW_ATHLETE_TARGET = '/hub/profile/setup?new=true';

/**
 * `brandPreview` is the server's verified read of the preview cookie, so the
 * first paint already says myHYROX under the Hyrox skin. Without it (undefined)
 * the page falls back to the client cookie check, which only lands after mount.
 */
export function MyFitventAuth({ brandPreview: serverBrand }: { brandPreview?: BrandPreviewId | null } = {}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTarget = safeRedirect(searchParams.get('redirect'));
  // A first-time athlete normally goes to profile setup. One who arrived from
  // outside the hub (a results page for registered athletes) goes back to
  // where they were: that page is why they signed in, and setup can wait.
  const newAthleteTarget = searchParams.get('redirect') && !redirectTarget.startsWith('/hub') ? redirectTarget : NEW_ATHLETE_TARGET;
  const isMobile = useIsMobile();
  const clientBrand = useBrandPreview();
  const brandPreview = serverBrand !== undefined ? serverBrand : clientBrand;
  const brandName = brandPreview === 'hyrox' ? 'myHYROX' : 'myfitvent';

  const [mode, setMode] = useState<MyFitventAuthMode>(() => parseAuthMode(searchParams.get('mode')));
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  // The hook keeps one error string across steps. Remember which step raised
  // it, so a bad code is not still showing after "Use a different email".
  const [errorStep, setErrorStep] = useState<string | null>(null);
  const [detectedCountry] = useState(() => detectCountryFromTimezone() as Country);

  // iOS Safari only raises the keyboard from a user gesture. Focusing this
  // hidden input during the Continue tap keeps the keyboard up across the
  // async hop to the code step, where focus moves to the real code field.
  const proxyInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const auth = useParticipantAuth({
    onSuccess: (isNewUser) => {
      const target = isNewUser ? newAthleteTarget : redirectTarget;
      // Refresh after moving so the hub layout reads the new session cookie
      // (it still holds the signed-out state from this page otherwise).
      setTimeout(() => {
        router.push(target);
        router.refresh();
      }, 1200);
    },
  });

  useEffect(() => {
    if (auth.error) setErrorStep(auth.step);
    // Only a new error re-tags the step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.error]);

  const serverError =
    auth.error && errorStep === auth.step
      ? plainAuthError(auth.error, auth.step === 'otp' ? 'code' : auth.step === 'phone-collect' ? 'phone' : 'email')
      : null;

  // Verify as soon as the sixth digit lands, typed, pasted or autofilled.
  useEffect(() => {
    if (isCompleteCode(auth.otpValue) && !auth.loading) auth.verifyOTP(auth.otpValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.otpValue]);

  // Focus by hand on every step change: iOS ignores autoFocus on mount.
  useEffect(() => {
    if (auth.step === 'email') requestAnimationFrame(() => emailInputRef.current?.focus());
    if (auth.step === 'otp') {
      setResent(false);
      requestAnimationFrame(() => codeInputRef.current?.focus());
    }
  }, [auth.step]);

  // A failed send leaves focus on the hidden proxy, and a wrong code clears
  // the field. Either way, put the cursor back where the fix goes.
  useEffect(() => {
    if (!serverError) return;
    if (auth.step === 'email') emailInputRef.current?.focus();
    if (auth.step === 'otp') codeInputRef.current?.focus();
  }, [auth.step, serverError]);

  const copy = emailStepCopy(mode, brandName);
  const signUp = mode === 'signup';
  const atEmail = auth.step === 'email';
  const showArt = signUp && atEmail;
  const signedIn = auth.step === 'phone-collect' || auth.step === 'success';

  function submitEmail() {
    if (auth.loading) return;
    // Read the field itself, not React state: iOS can fire the submit before
    // the last onChange has reached state, which used to send an empty email.
    const value = (emailInputRef.current?.value ?? auth.email).trim();
    const problem = emailError(value);
    setEmailErr(problem);
    if (problem) {
      emailInputRef.current?.focus();
      return;
    }
    proxyInputRef.current?.focus();
    auth.submitEmail(value);
  }

  function switchMode() {
    setMode((m) => (m === 'signup' ? 'signin' : 'signup'));
    setEmailErr(null);
    setErrorStep(null);
    auth.setOtpValue('');
    auth.setStep('email');
  }

  function backToEmail() {
    setErrorStep(null);
    auth.setOtpValue('');
    auth.setStep('email');
  }

  /** Part-way into sign up, the header becomes "their email, Sign out". */
  function signOutOfSignUp() {
    auth.reset();
    setEmailErr(null);
    setErrorStep(null);
    setResent(false);
    setMode('signin');
  }

  async function resend() {
    const sent = await auth.resendOTP();
    setResent(sent);
    codeInputRef.current?.focus();
  }

  // ── Header ──
  let headerPrompt: React.ReactNode = undefined;
  let headerAction: { label: string; onClick: () => void } | undefined;
  if (!signedIn) {
    if (signUp && auth.step === 'otp') {
      headerPrompt = auth.email;
      headerAction = { label: 'Sign out', onClick: signOutOfSignUp };
    } else if (signUp) {
      headerPrompt = 'Already have an account?';
      headerAction = { label: 'Sign in', onClick: switchMode };
    } else {
      headerPrompt = `New to ${brandName}?`;
      headerAction = { label: 'Create an account', onClick: switchMode };
    }
  }

  const inputHeight = isMobile ? 48 : 42;
  const inputFont = isMobile ? 16 : 14;

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--fvd-canvas)',
        color: 'var(--fvd-ink)',
        fontFamily: 'var(--fvd-font-body)',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <AuthHeader brandPreview={brandPreview} prompt={headerPrompt} action={headerAction} />

      <main
        className={
          showArt
            ? 'grid flex-1 grid-cols-[minmax(0,1fr)] items-start gap-14 px-4 pt-8 pb-10 min-[600px]:px-5 min-[600px]:pt-10 min-[981px]:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] min-[981px]:items-center min-[981px]:py-2 min-[981px]:pr-0 min-[981px]:pl-10'
            : 'grid flex-1 grid-cols-[minmax(0,1fr)] items-start px-4 pt-8 pb-10 min-[600px]:px-5 min-[600px]:pt-14'
        }
      >
        <div style={{ width: '100%', maxWidth: 380, justifySelf: 'center' }}>
          {/* ── Email ── */}
          {atEmail && (
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                submitEmail();
              }}
            >
              <AuthTitle large={signUp}>{copy.title}</AuthTitle>
              <AuthIntro>{copy.intro}</AuthIntro>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 28 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Email</span>
                <input
                  ref={emailInputRef}
                  type="email"
                  inputMode="email"
                  enterKeyHint="go"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={auth.email}
                  onChange={(e) => {
                    auth.setEmail(e.target.value);
                    if (emailErr) setEmailErr(null);
                  }}
                  placeholder="you@example.com"
                  aria-invalid={!!(emailErr || serverError) || undefined}
                  aria-describedby="mfv-email-msg"
                  className={fieldClass(!!(emailErr || serverError))}
                  style={{
                    height: inputHeight,
                    padding: '0 13px',
                    borderRadius: 9,
                    background: 'var(--fvd-canvas)',
                    fontSize: inputFont,
                    color: 'var(--fvd-ink)',
                    fontFamily: 'inherit',
                  }}
                />
              </label>
              {emailErr || serverError ? (
                <FieldMessage id="mfv-email-msg" tone="error">
                  {emailErr || serverError}
                </FieldMessage>
              ) : emailHint(mode, auth.userInfo) ? (
                <FieldMessage id="mfv-email-msg" tone="hint">
                  {emailHint(mode, auth.userInfo)}
                </FieldMessage>
              ) : null}

              <PrimaryButton isMobile={isMobile} loading={auth.loading}>
                {auth.loading ? 'Sending your code' : 'Continue'}
              </PrimaryButton>

              {signUp && (
                <p
                  style={{
                    margin: '16px 0 0',
                    fontSize: 12.5,
                    color: 'var(--fvd-ink-muted)',
                    lineHeight: 1.6,
                    textWrap: 'pretty',
                  }}
                >
                  By continuing you agree to the{' '}
                  <Link href="/terms" className="fvd-link" style={{ color: 'var(--fvd-ink)' }}>
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" className="fvd-link" style={{ color: 'var(--fvd-ink)' }}>
                    Privacy Policy
                  </Link>
                  .
                </p>
              )}

              <OrganiserNote />
            </form>
          )}

          {/* ── Code ── */}
          {auth.step === 'otp' && (
            <CodeStep
              mode={mode}
              channel={auth.otpChannel}
              email={auth.email}
              maskedDestination={auth.maskedDestination}
              code={auth.otpValue}
              onCode={(v) => auth.setOtpValue(sanitiseCode(v))}
              onSubmit={() => {
                if (isCompleteCode(auth.otpValue) && !auth.loading) auth.verifyOTP(auth.otpValue);
              }}
              loading={auth.loading}
              error={serverError}
              resent={resent}
              cooldown={auth.resendCooldown}
              onResend={resend}
              onBack={backToEmail}
              inputRef={codeInputRef}
              isMobile={isMobile}
            />
          )}

          {/* ── Phone (new athletes, optional) ── */}
          {auth.step === 'phone-collect' && (
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (auth.phone.trim() && !auth.loading) auth.submitPhone(auth.phone);
              }}
            >
              <AuthIconTile>
                <Smartphone width={20} height={20} strokeWidth={1.8} />
              </AuthIconTile>
              <AuthTitle gap={18}>Add your phone number</AuthTitle>
              <AuthIntro>
                Optional. We&rsquo;ll save it to your profile, so your race entries fill it in for you.
              </AuthIntro>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 24 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Phone number</span>
                <PhoneInputField
                  v8
                  value={auth.phone}
                  onChange={(v) => auth.setPhone(v || '')}
                  defaultCountry={detectedCountry}
                  style={{
                    // The same size and ground as the email field on this page.
                    height: inputHeight,
                    borderRadius: 9,
                    background: 'var(--fvd-canvas)',
                    fontSize: inputFont,
                    ...(serverError ? { borderColor: 'var(--fvd-danger)' } : {}),
                  }}
                />
              </div>
              {serverError && <FieldMessage tone="error">{serverError}</FieldMessage>}

              <PrimaryButton isMobile={isMobile} loading={auth.loading} disabled={!auth.phone.trim()}>
                {auth.loading ? 'Saving' : 'Continue'}
              </PrimaryButton>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, fontSize: 13 }}>
                <span style={{ color: 'var(--fvd-ink-muted)' }}>
                  {auth.phone.trim() ? 'Ready to save.' : 'Add a number, or skip it.'}
                </span>
                <span style={{ flex: 1 }} />
                <TextAction onClick={auth.skipPhone} disabled={auth.loading}>
                  Skip for now
                </TextAction>
              </div>
            </form>
          )}

          {/* ── Done ── */}
          {auth.step === 'success' && (
            <div aria-live="polite">
              <AuthIconTile round>
                <Check width={20} height={20} strokeWidth={2.4} />
              </AuthIconTile>
              <AuthTitle gap={18}>
                {auth.isNewUser
                  ? "You're in"
                  : auth.participant?.firstName
                    ? `Welcome back, ${auth.participant.firstName}`
                    : 'Welcome back'}
              </AuthTitle>
              <AuthIntro>
                {auth.isNewUser
                  ? 'Your email is confirmed. Next, a few details for your profile.'
                  : 'Taking you to your races.'}
              </AuthIntro>
              <Link
                href={auth.isNewUser ? newAthleteTarget : redirectTarget}
                className="fvd-hoverable-solid"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: isMobile ? 48 : 42,
                  marginTop: 24,
                  borderRadius: 9,
                  background: 'var(--fvd-teal-solid)',
                  color: 'var(--fvd-teal-solid-on)',
                  fontSize: 14,
                  fontWeight: 500,
                  textDecoration: 'none',
                }}
              >
                Continue
              </Link>
            </div>
          )}

          <input
            ref={proxyInputRef}
            inputMode="numeric"
            aria-hidden="true"
            tabIndex={-1}
            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
          />
        </div>

        {showArt && <AthleteArtPanel />}
      </main>

      <AuthFooter />
    </div>
  );
}

function CodeStep({
  mode,
  channel,
  email,
  maskedDestination,
  code,
  onCode,
  onSubmit,
  loading,
  error,
  resent,
  cooldown,
  onResend,
  onBack,
  inputRef,
  isMobile,
}: {
  mode: MyFitventAuthMode;
  channel: 'email' | 'sms';
  email: string;
  maskedDestination: string;
  code: string;
  onCode: (raw: string) => void;
  onSubmit: () => void;
  loading: boolean;
  error: string | null;
  resent: boolean;
  cooldown: number;
  onResend: () => void;
  onBack: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isMobile: boolean;
}) {
  const copy = codeStepCopy(mode, channel);
  const ready = isCompleteCode(code);
  const ending = phoneEnding(maskedDestination);
  const strong = { color: 'var(--fvd-ink)', fontWeight: 500 } as const;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <AuthIconTile>
        {channel === 'sms' ? (
          <MessageSquareText width={20} height={20} strokeWidth={1.8} />
        ) : (
          <Mail width={20} height={20} strokeWidth={1.8} />
        )}
      </AuthIconTile>
      <AuthTitle gap={18}>{copy.title}</AuthTitle>
      <AuthIntro>
        {channel === 'sms' ? (
          <>
            We texted a 6-digit code to {ending ? <>the number ending <span style={strong}>{ending}</span></> : 'your phone'}.
            It works for 10 minutes.
          </>
        ) : (
          <>
            We sent a 6-digit code to <span style={{ ...strong, overflowWrap: 'anywhere' }}>{email}</span>. It works
            for 10 minutes.
          </>
        )}
      </AuthIntro>

      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        enterKeyHint="go"
        // No maxLength: it would cut a pasted "123 456" short before the
        // sanitiser could strip the space.
        value={code}
        onChange={(e) => onCode(e.target.value)}
        placeholder="000000"
        aria-label="6-digit code"
        aria-invalid={!!error || undefined}
        aria-describedby="mfv-code-msg"
        // readOnly, not disabled, while checking: disabling drops focus and
        // closes the phone keyboard, so a wrong code would mean tapping back in.
        readOnly={loading}
        className={fieldClass(!!error)}
        style={{
          height: 54,
          marginTop: 24,
          // The extra left padding balances the trailing letter-spacing so
          // the six digits sit dead centre.
          padding: '0 16px 0 calc(16px + .42em)',
          borderRadius: 10,
          background: 'var(--fvd-canvas)',
          fontFamily: 'var(--fvd-font-mono)',
          fontSize: 24,
          letterSpacing: '.42em',
          textAlign: 'center',
          color: 'var(--fvd-ink)',
          opacity: loading ? 0.7 : 1,
        }}
      />
      {error ? (
        <FieldMessage id="mfv-code-msg" tone="error">
          {error}
        </FieldMessage>
      ) : !ready ? (
        <span id="mfv-code-msg" className="sr-only">
          Enter all 6 digits to continue.
        </span>
      ) : null}

      <PrimaryButton isMobile={isMobile} loading={loading} disabled={!ready}>
        {loading ? 'Checking your code' : copy.action}
      </PrimaryButton>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginTop: 18,
          fontSize: 13,
          color: 'var(--fvd-ink-secondary)',
          lineHeight: 1.6,
        }}
      >
        <span aria-live="polite">{resent ? resentNote(channel) : "Didn't get it?"}</span>
        {cooldown > 0 ? (
          !resent && <span style={{ color: 'var(--fvd-ink-muted)' }}>Send it again in {cooldown}s</span>
        ) : (
          <TextAction onClick={onResend} disabled={loading}>
            Send it again
          </TextAction>
        )}
        <span aria-hidden="true" style={{ color: 'var(--fvd-ink-disabled)' }}>
          ·
        </span>
        <TextAction onClick={onBack} disabled={loading}>
          Use a different email
        </TextAction>
      </div>
    </form>
  );
}
